import { Image } from 'expo-image';
import { XStack, YStack } from 'tamagui';

import { PassGlow, PlanBadge } from '@/components/MembershipPlanCard';
import PSText from '@/components/ui/PSText';
import type { MembershipStatus, UserMembership } from '@/src/api/authApi';
import { hasUsedUpTrial } from '@/src/lib/membershipUpsell';
import { formatDateWithYear } from '@/src/utils/dateFormatter';

/**
 * The precise state, for the row that has no dates to show instead. Typed
 * against the whole union so a status added to the wire won't compile until
 * someone decides what this card calls it.
 */
const STATUS_LABEL: Record<MembershipStatus, string> = {
  // Not "Confirming payment": the wire can't tell a purchase that was never
  // finished from one whose webhook is late, and only one of those is being
  // confirmed.
  pending: 'Payment pending',
  active: 'Active',
  cancelling: 'Cancelling',
  cancelled: 'Cancelled',
  paused: 'Paused',
  pending_retry: 'Retrying payment',
  halted: 'Halted',
  completed: 'Completed',
  expired: 'Expired',
};

type Column = { label: string; value: string };

/**
 * `phase` is what branches, not `status` — the server already folded status and
 * the billing cycle into it. `expiresAt` is read before `renewsAt` because the
 * two are mutually exclusive on the wire and only a live subscription renews.
 * A used-up trial drops both: there's nothing left to be valid for.
 */
function membershipColumns(membership: UserMembership): Column[] {
  const { phase, status, activatedAt, expiresAt, renewsAt, daysRemaining } =
    membership;
  const columns: Column[] = [];
  const usedUp = hasUsedUpTrial(membership);

  if (activatedAt)
    columns.push({
      label: phase === 'upcoming' ? 'Starts' : 'Started',
      value: formatDateWithYear(activatedAt),
    });

  if (usedUp) {
    // Nothing to count down to.
  } else if (expiresAt) {
    const ending = status === 'cancelled' || status === 'cancelling';
    columns.push({
      label: phase === 'past' ? 'Ended' : ending ? 'Ends' : 'Valid till',
      value: formatDateWithYear(expiresAt),
    });
  } else if (renewsAt) {
    columns.push({ label: 'Renews', value: formatDateWithYear(renewsAt) });
  }

  // Only while it's still counting down: a past row carries a stale 0.
  if (!usedUp && phase === 'current' && daysRemaining !== null)
    columns.push({
      label: 'Remaining',
      value: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`,
    });

  if (columns.length === 0)
    columns.push({ label: 'Status', value: STATUS_LABEL[status] });

  return columns;
}

const TONE_BG = {
  // The glow drawn over it is translucent; this is the base it's tuned on.
  current: '$brandDark',
  upcoming: '$brandDark',
  past: '$brandGray',
} as const satisfies Record<UserMembership['phase'], string>;

// A factor, not an opacity, so a card that already rests faded still reacts.
const PRESS_DIM = 0.5;

/**
 * A cancelled membership FADES rather than going grey: it's still live until
 * the cycle they paid for ends. Grey is what `past` gets.
 */
const ENDING_OPACITY = 0.6;

const isEnding = (membership: UserMembership) =>
  membership.phase === 'current' &&
  (membership.status === 'cancelling' || membership.status === 'cancelled');

/**
 * Which section a row files under, and therefore which surface it draws on.
 *
 * `phase` decides, with ONE override: a purchase cancelled before its first
 * charge keeps `phase: 'upcoming'` for good (the server derives phase from the
 * latest billing cycle, and it never had one), so it would otherwise keep
 * promising a pass that is never coming.
 */
export const sectionOf = (
  membership: UserMembership
): UserMembership['phase'] =>
  membership.phase === 'upcoming' &&
  (membership.status === 'cancelling' || membership.status === 'cancelled')
    ? 'past'
    : membership.phase;

export default function MembershipCard({
  membership,
  onPress,
}: {
  membership: UserMembership;
  onPress?: () => void;
}) {
  const phase = sectionOf(membership);
  const columns = membershipColumns(membership);
  const opacity = isEnding(membership) ? ENDING_OPACITY : 1;

  return (
    <YStack
      alignSelf="stretch"
      bg={TONE_BG[phase]}
      rounded="$base"
      overflow="hidden"
      opacity={opacity}
      cursor={onPress ? 'pointer' : undefined}
      pressStyle={onPress ? { opacity: opacity * PRESS_DIM } : undefined}
      onPress={onPress}
    >
      {phase === 'current' && <PassGlow kind={membership.plan.kind} />}

      {/* Padding lives here, not on the card: an absolute child's 100% size
          resolves against the content box, so the glow would stop short. */}
      <YStack p="$lg">
        <Image
          source={require('@/assets/images/access-pass-logo.png')}
          style={{ width: 200, height: 60 }}
          contentFit="contain"
        />
        <PSText.Title color="$textWhite" numberOfLines={2} mt="$space.lg">
          {membership.plan.name}
        </PSText.Title>
        <XStack mt="$space.xl" gap="$xl" items="center">
          {columns.map((column) => (
            <YStack key={column.label} gap="$xs">
              <PSText.H3 color="$textWhiteMuted">{column.label}</PSText.H3>
              <PSText.Body3 color="$textWhite">{column.value}</PSText.Body3>
            </YStack>
          ))}
        </XStack>
      </YStack>

      {/* The dates above still read as a pass to use; this says there's
          nothing left on it. */}
      {hasUsedUpTrial(membership) && (
        <YStack bg="$panelTranslucent" px="$lg" py="$md">
          <PSText.Body3 color="$textWhite">
            {`You've used up your ${membership.plan.name} perks`}
          </PSText.Body3>
        </YStack>
      )}

      <PlanBadge kind={membership.plan.kind} />
    </YStack>
  );
}
